import type { Database } from '../../../../lib/database.types';

export type PageRow = Database['public']['Tables']['client_pages']['Row'];
export type PageQueryRow = Database['public']['Tables']['client_page_queries']['Row'];
export type PageHistoryRow = Database['public']['Tables']['client_page_history']['Row'];

export const fmtInt = (n: number | null | undefined) => (n == null ? '—' : Math.round(n).toLocaleString('en-US'));
export const fmtDec = (n: number | null | undefined, digits = 1) => (n == null ? '—' : Number(n).toFixed(digits));
/** ctr is stored as a percentage already (4.41 = 4.41%). */
export const fmtPct = (n: number | null | undefined) => (n == null ? '—' : `${Number(n).toFixed(Number(n) >= 10 ? 0 : 1)}%`);
export const fmtWhen = (iso: string | null) => {
  if (!iso) return '—';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return days <= 0 ? 'today' : `${days}d ago`;
};
/** A date column (YYYY-MM-DD) as "Aug 19" — parsed as local so it doesn't slip a day. */
export const fmtDay = (day: string | null) => {
  if (!day) return '—';
  const [y, m, d] = day.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const hostOf = (url: string) => {
  try {
    return new URL(url).host;
  } catch {
    return '';
  }
};

/** The path, or host + path when the URL isn't on the site's main host (a subdomain GSC reported). */
export const pathOf = (url: string, mainHost: string) => {
  try {
    const u = new URL(url);
    const path = `${u.pathname}${u.search}` || '/';
    return u.host === mainHost ? path : `${u.host}${path}`;
  } catch {
    return url;
  }
};

export const ctrOf = (clicks: number | null, impressions: number | null) =>
  impressions ? (100 * (clicks ?? 0)) / impressions : null;

export type OveroptLevel = 'over' | 'under';

/** One phrase the keyword density screen counted on the page (scripts/lib/overopt.mjs). */
export interface OveroptPhrase {
  phrase: string;
  sources: string[];
  /** The search the page is built for; only the target can be under-optimized. */
  target?: boolean;
  uses: number;
  /** Word share of the body, as a percentage. */
  density: number;
  /** H1–H3 holding every word of the phrase. */
  headings: number;
  heading_share: number;
  in_title: boolean;
  in_h1: boolean;
  in_url: boolean;
  impressions: number | null;
  position: number | null;
  level: OveroptLevel | null;
}

export interface PageOveropt {
  level: OveroptLevel | null;
  words?: number;
  headings?: number;
  h1?: string;
  target?: string | null;
  phrases: OveroptPhrase[];
  error?: string;
  /** Set when the live domain blocked the check and the page was read from its Netlify address. */
  fetched_from?: string;
}

export const overoptOf = (row: PageRow): PageOveropt | null => (row.overopt as unknown as PageOveropt | null) ?? null;

/** The templated-content screen's result for a page (scripts/lib/sameness.mjs). share is 0–1. */
export interface PageTemplate {
  share: number | null;
  sibling_url: string | null;
  siblings: { url: string; share: number }[];
  shingles: number;
  level: 'templated' | null;
}

export const templateOf = (row: PageRow): PageTemplate | null => (row.template as unknown as PageTemplate | null) ?? null;

/** Anything about the page that explains why it might not be performing. Empty = a healthy live page. */
/**
 * The floor for internal links pointing at a page (Kyle, 2026-10-11). Under it, the page gets a "few links in" flag
 * and an Internal Links Fix can add links from relevant pages (skill internal-links-fix). Counted by the Ahrefs crawl.
 */
export const LINK_FLOOR = 5;
/** A live, indexable page with fewer than LINK_FLOOR internal links pointing at it. */
export const isUnderLinked = (row: PageRow): boolean =>
  !!row.in_crawl && (row.http_code == null || row.http_code === 200) && !row.is_noindex && row.inlinks != null && row.inlinks < LINK_FLOOR;

export const pageFlags = (row: PageRow): string[] => {
  const flags: string[] = [];
  if (!row.in_crawl) flags.push('not in crawl');
  if (row.http_code != null && row.http_code !== 200) flags.push(String(row.http_code));
  if (row.is_noindex) flags.push('noindex');
  else if (row.in_crawl && row.is_indexable === false) flags.push('not indexable');
  if (row.in_crawl && row.canonical && row.canonical !== row.url) flags.push('canonicalised');
  if (row.overopt_level === 'over') flags.push('over-opt');
  if (row.overopt_level === 'under') flags.push('under-opt');
  if (row.template_level === 'templated') flags.push('templated');
  if (isUnderLinked(row)) flags.push('few links in');
  return flags;
};
